import React, {
  createContext,
  useContext,
  useEffect,
  useState,
} from "react";

import {
  useDispatch,
  useSelector,
} from "react-redux";

import axios from "axios";

import {
  createDiningSession,
  SESSION_SUCCESS,
} from "../actions/sessionActions";


const RestaurantSessionContext =
  createContext(null);


export function RestaurantSessionProvider({
  restaurantId,
  tableId,
  children,
}) {

  const dispatch = useDispatch();


  const {
    loading: reduxLoading,
    sessionToken,
    sessionId,
    error: reduxError,
  } = useSelector(
    (state) => state.session
  );


  // =====================================================
  // SESSION STORAGE KEY
  // =====================================================

  const SESSION_STORAGE_KEY =
    `restaurant_session_${restaurantId}_${tableId}`;


  // =====================================================
  // LOCAL SESSION STATE
  // =====================================================

  const [
    initializing,
    setInitializing,
  ] = useState(true);


  const [
    sessionError,
    setSessionError,
  ] = useState(null);



  // =====================================================
  // STEP 3B
  // LOAD / VALIDATE / CREATE SESSION
  // =====================================================

  useEffect(() => {

    let cancelled = false;


    const initializeSession =
      async () => {

        if (
          !restaurantId ||
          !tableId
        ) {

          setInitializing(false);

          return;

        }


        setInitializing(true);

        setSessionError(null);


        try {

          // =================================================
          // 1. CHECK SAVED SESSION
          // =================================================

          const savedSessionToken =
            sessionStorage.getItem(
              SESSION_STORAGE_KEY
            );


          // =================================================
          // 2. IF SESSION EXISTS, VALIDATE IT
          // =================================================

          if (savedSessionToken) {

            try {

              const response =
                await axios.post(

                  `/api/restaurants/${restaurantId}/tables/${tableId}/session/validate/`,

                  {
                    session_token:
                      savedSessionToken,
                  }

                );


              // =============================================
              // VALID SESSION
              // =============================================

              if (
                response.data?.valid === true
              ) {

                if (cancelled) {
                  return;
                }


                dispatch({
                  type: SESSION_SUCCESS,
                  payload: {
                    session_id:
                      response.data.session_id,

                    session_token:
                      response.data.session_token,

                    restaurant_id:
                      response.data.restaurant_id,

                    table_id:
                      response.data.table_id,

                    is_active:
                      response.data.is_active,
                  },
                });


                setInitializing(false);

                return;

              }


              // =============================================
              // INVALID SESSION
              // =============================================

              sessionStorage.removeItem(
                SESSION_STORAGE_KEY
              );

            } catch (validationError) {

              console.warn(
                "Saved dining session validation failed:",
                validationError
              );


              // Remove invalid/corrupted
              // session token.

              sessionStorage.removeItem(
                SESSION_STORAGE_KEY
              );

            }

          }


          // =================================================
          // 3. NO VALID SESSION
          //    CREATE A NEW SESSION
          // =================================================

          const newSession =
            await dispatch(
              createDiningSession(
                restaurantId,
                tableId
              )
            );


          if (cancelled) {
            return;
          }


          // =================================================
          // 4. SAVE NEW SESSION TOKEN
          // =================================================

          if (
            newSession?.session_token
          ) {

            sessionStorage.setItem(
              SESSION_STORAGE_KEY,
              newSession.session_token
            );

          }


          setInitializing(false);

        } catch (error) {

          console.error(
            "Failed to initialize dining session:",
            error
          );


          if (cancelled) {
            return;
          }


          setSessionError(

            error?.response?.data?.detail ||

            error?.message ||

            "Unable to start dining session."

          );


          setInitializing(false);

        }

      };


    initializeSession();


    return () => {

      cancelled = true;

    };

  }, [
    dispatch,
    restaurantId,
    tableId,
    SESSION_STORAGE_KEY,
  ]);



  // =====================================================
  // LOADING
  // =====================================================

  if (
    initializing ||
    reduxLoading
  ) {

    return (
      <div>
        Starting your table session...
      </div>
    );

  }



  // =====================================================
  // ERROR
  // =====================================================

  if (
    sessionError ||
    reduxError
  ) {

    return (
      <div>
        Unable to start dining session.
        <br />
        {sessionError || reduxError}
      </div>
    );

  }



  // =====================================================
  // WAITING FOR SESSION
  // =====================================================

  if (!sessionToken) {

    return (
      <div>
        Starting your table session...
      </div>
    );

  }



  // =====================================================
  // PROVIDER
  // =====================================================

  return (
    <RestaurantSessionContext.Provider
      value={{
        sessionId,
        sessionToken,
        restaurantId,
        tableId,
        SESSION_STORAGE_KEY,
      }}
    >

      {children}

    </RestaurantSessionContext.Provider>
  );

}



export function useRestaurantSession() {

  return useContext(
    RestaurantSessionContext
  );

}